// Hostname routing: `jobs.<domain>/nursing` is the file route `/jobs-preview/<domain>/nursing`
// (also a real address for staff previews), and `ppl-ai.com/pricing` is `/v2/pricing`. `rewriteIn`
// maps host to route, `rewriteOut` maps back before the address bar shows it.

import { PRODUCT_ORIGIN } from "@/lib/marketing/origins";
import { PEOPLE_PAGES } from "@/lib/session/people-pages";

const PREFIX = "jobs.";
const ROUTE = "/jobs-preview/";

const PEOPLE_HOSTS = new Set(["ppl-ai.com", "www.ppl-ai.com"]);
const PEOPLE_ROUTE = "/v2";

export function isPeopleHost(hostname: string): boolean {
  return PEOPLE_HOSTS.has(hostname);
}

function firstSegment(pathname: string): string {
  return pathname.split("/")[1] ?? "";
}

// `known` (first path segments with a route) comes from router.tsx, since this module cannot import
// the route tree. With it, an unknown address on PPL's host is pulled into the arm so its catch-all
// answers with PPL's own 404.
function peopleIn(url: URL, known?: ReadonlySet<string>): URL | undefined {
  if (url.pathname === PEOPLE_ROUTE || url.pathname.startsWith(`${PEOPLE_ROUTE}/`)) {
    return undefined;
  }
  const segment = firstSegment(url.pathname);
  const isArmPage = url.pathname === "/" || PEOPLE_PAGES.includes(segment);
  const isStranger = known !== undefined && !known.has(segment);
  if (!isArmPage && !isStranger) return undefined;
  const rewritten = new URL(url);
  // A trailing slash on the root would otherwise produce a double one.
  const rest = url.pathname === "/" ? "" : url.pathname;
  rewritten.pathname = `${PEOPLE_ROUTE}${rest}`;
  return rewritten;
}

function peopleOut(url: URL): URL | undefined {
  if (url.pathname !== PEOPLE_ROUTE && !url.pathname.startsWith(`${PEOPLE_ROUTE}/`)) {
    return undefined;
  }
  const rewritten = new URL(url);
  rewritten.pathname = url.pathname.slice(PEOPLE_ROUTE.length) || "/";
  return rewritten;
}

// Keep in step with `PEOPLE_ORIGIN`, which the canonical tags use.
const PEOPLE_HOME = "https://ppl-ai.com";
const PEOPLE_APEX = "ppl-ai.com";

// www and plain http answer a permanent redirect to the apex before the router (server.ts): one
// page at two addresses splits search, and a canonical pointing at a redirecting address is only a
// hint. `proto` is `x-forwarded-proto`; the request URL behind the edge is always http.
export function peopleCanonicalAddress(url: URL, proto: string): string | undefined {
  if (!PEOPLE_HOSTS.has(url.hostname)) return undefined;
  if (url.hostname === PEOPLE_APEX && proto === "https") return undefined;
  return `${PEOPLE_HOME}${url.pathname}${url.search}`;
}

// Any other host (localhost, a preview) keeps serving `/v2` directly, so the arm can be worked on
// where PEOPLE's domain is not owned.
const LIVE_SITE_HOSTS = new Set(["assisted-care.com", "www.assisted-care.com"]);

export function isLiveSiteHost(hostname: string): boolean {
  return LIVE_SITE_HOSTS.has(hostname);
}

// `assisted-care.com/v2/pricing?ref=x` is a 301 to `https://ppl-ai.com/pricing?ref=x`. The server
// answers it before the router (server.ts) for crawlers; the arm's layout (routes/v2/route.tsx)
// throws the same redirect for client-side navigations.
export function retiredArmHome(url: URL): string | undefined {
  if (!LIVE_SITE_HOSTS.has(url.hostname)) return undefined;
  const rest = peopleOut(url);
  if (!rest) return undefined;
  return `${PEOPLE_HOME}${rest.pathname}${rest.search}`;
}

// The product's host is a sign-in host wherever it lives, but never ppl-ai.com itself: those
// redirects point there, so they would loop.
const PRODUCT_HOST = new URL(PRODUCT_ORIGIN).hostname;
const SIGN_IN_HOSTS = [
  "login.ppl-ai.com",
  ...(PEOPLE_HOSTS.has(PRODUCT_HOST) ? [] : [PRODUCT_HOST]),
];

const COPY_HOSTS = new Set([
  ...LIVE_SITE_HOSTS,
  "login.assisted-care.com",
  ...SIGN_IN_HOSTS,
  ...PEOPLE_HOSTS,
]);
const RETIRED_COPY = /^\/v[13](\/.*)?$/;

export function retiredCopyHome(url: URL): string | undefined {
  if (!COPY_HOSTS.has(url.hostname)) return undefined;
  const match = RETIRED_COPY.exec(url.pathname);
  if (!match) return undefined;
  return `${PEOPLE_HOME}${match[1] ?? "/"}${url.search}`;
}

// Retired pages 301 to the same page on ppl-ai.com so their search standing moves too.
const RETIRED_SITE_HOSTS = new Set([
  ...LIVE_SITE_HOSTS,
  "login.assisted-care.com",
  ...SIGN_IN_HOSTS,
]);
export const RETIRED_SITE_PAGES = [
  "about",
  "contact",
  "demo",
  "experience",
  "features",
  "inquire",
  "pricing",
  "security",
  "solutions",
];

export function retiredSiteHome(url: URL): string | undefined {
  if (!RETIRED_SITE_HOSTS.has(url.hostname)) return undefined;
  if (!RETIRED_SITE_PAGES.includes(firstSegment(url.pathname))) return undefined;
  return `${PEOPLE_HOME}${url.pathname}${url.search}`;
}

// `jobs.<our domain>` is no building's careers site, but the hiring team hands it to candidates to
// sign in, so it goes to the sign-in page.
const OUR_OWN_DOMAINS = new Set(["assisted-care.com", "ppl-ai.com"]);

export function isPlatformJobsHost(hostname: string): boolean {
  const domain = jobsDomainOf(hostname);
  return domain !== null && OUR_OWN_DOMAINS.has(domain);
}

export function jobsDomainOf(hostname: string): string | null {
  if (!hostname.startsWith(PREFIX)) return null;
  const domain = hostname.slice(PREFIX.length);
  return domain === "" ? null : domain;
}

// The app's doors, which a jobs host must not serve: the session cookie is set for the app's host
// and never reaches `jobs.<domain>`, so a sign-in there would answer 200 and leave the candidate
// signed out. The server redirects these to the app (`productDoorFor`).
const PRODUCT_DOORS = [
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/set-new-password",
  "/applicant-portal",
  "/sign/",
  "/signing/",
  "/admission-sign/",
  "/pay/",
];

const isDoor = (pathname: string) =>
  PRODUCT_DOORS.some(
    (door) =>
      pathname === door ||
      pathname === door.replace(/\/$/, "") ||
      pathname.startsWith(door.endsWith("/") ? door : `${door}/`),
  );

export function productDoorFor(url: URL): string | undefined {
  if (!jobsDomainOf(url.hostname)) return undefined;
  if (isPlatformJobsHost(url.hostname) && url.pathname === "/") {
    return `${PRODUCT_ORIGIN}/login${url.search}`;
  }
  if (!isDoor(url.pathname)) return undefined;
  return `${PRODUCT_ORIGIN}${url.pathname}${url.search}`;
}

// Left alone on a jobs host: `/careers/…` (Apply keeps the hostname) and the doors the server
// already sent to the app.
const PASS_THROUGH = [
  "/careers",
  "/applicant-portal",
  "/sign/",
  "/signing/",
  "/admission-sign/",
  "/pay/",
];

export function rewriteIn({ url }: { url: URL }, known?: ReadonlySet<string>): URL | undefined {
  if (isPeopleHost(url.hostname)) return peopleIn(url, known);
  const domain = jobsDomainOf(url.hostname);
  if (!domain) return undefined;
  if (url.pathname.startsWith(ROUTE)) return undefined;
  // Our own jobs host's root goes to sign-in, not a listing that would say "Jobs page not found".
  if (isPlatformJobsHost(url.hostname) && url.pathname === "/") {
    const rewritten = new URL(url);
    rewritten.pathname = "/login";
    return rewritten;
  }
  if (PASS_THROUGH.some((path) => url.pathname === path || url.pathname.startsWith(path))) {
    return undefined;
  }
  const rewritten = new URL(url);
  // A trailing slash on the root would otherwise produce a double one.
  const rest = url.pathname === "/" ? "" : url.pathname;
  rewritten.pathname = `${ROUTE}${domain}${rest}`;
  return rewritten;
}

export function rewriteOut({ url }: { url: URL }): URL | undefined {
  if (isPeopleHost(url.hostname)) return peopleOut(url);
  const domain = jobsDomainOf(url.hostname);
  if (!domain) return undefined;
  const expected = `${ROUTE}${domain}`;
  if (!url.pathname.startsWith(expected)) return undefined;
  const rewritten = new URL(url);
  rewritten.pathname = url.pathname.slice(expected.length) || "/";
  return rewritten;
}
